import { MEMBERSHIP_TIMING, RTC_PROTOCOL_V1, RTC_SESSION_EVENTS } from '../protocol/constants.js';
import { RtcError, silentLogger, toRtcError, } from '../protocol/types.js';
import { hashedRoomName, memberIdFor } from '../identity/identity.js';
import { TypedEmitter } from '../util/emitter.js';
const asIntent = (value) => value === 'audio' || value === 'video' ? value : undefined;
export class JsSdkSignalling {
    constructor(options) {
        this.options = options;
        this.emitter = new TypedEmitter();
        this.subscriptions = [];
        this.state = 'idle';
        this.disposed = false;
        const userId = options.client.getUserId();
        const deviceId = options.client.getDeviceId();
        if (!userId || !deviceId) {
            throw new RtcError('unauthorized', 'Matrix client has no user or device id');
        }
        const room = options.client.getRoom(options.roomId);
        if (!room) {
            throw new RtcError('invalid_request', 'Room is not known to the Matrix client');
        }
        this.roomId = options.roomId;
        this.localUserId = userId;
        this.localDeviceId = deviceId;
        this.logger = options.logger ?? silentLogger;
        this.session = options.client.matrixRTC.getRoomSession(room);
        this.subscribe(RTC_SESSION_EVENTS.membershipsChanged, () => this.emitter.emit('participants', this.participants()));
        this.subscribe(RTC_SESSION_EVENTS.joinStateChanged, ((joined) => {
            this.setState(joined ? 'joined' : this.state === 'error' ? 'error' : 'left');
        }));
        this.subscribe(RTC_SESSION_EVENTS.encryptionKeyChanged, ((key, index, membership, rtcBackendIdentity) => {
            this.emitter.emit('mediaKey', {
                livekitIdentity: rtcBackendIdentity,
                key,
                index,
                userId: membership.userId,
                deviceId: membership.deviceId,
            });
        }));
        this.subscribe(RTC_SESSION_EVENTS.membershipManagerError, ((error) => {
            this.logger.error('rtc.signalling.membership_error', { error: String(error) });
            this.setState('error');
            this.emitter.emit('error', toRtcError(error, 'membership_failed'));
        }));
        this.subscribe(RTC_SESSION_EVENTS.didSendCallNotification, ((content) => {
            if (typeof content?.event_id === 'string') {
                this.emitter.emit('ringSent', content.event_id);
            }
        }));
    }
    participants() {
        return this.session.memberships.map(membership => ({
            userId: membership.sender,
            deviceId: membership.deviceId,
            memberId: membership.memberId,
            livekitIdentity: membership.rtcBackendIdentity,
            intent: asIntent(membership.callIntent),
            joinedAt: membership.createdTs(),
            isLocal: membership.sender === this.localUserId && membership.deviceId === this.localDeviceId,
        }));
    }
    consensusIntent() {
        return asIntent(this.session.getConsensusCallIntent());
    }
    isJoined() {
        return this.session.isJoined();
    }
    async join(options) {
        this.assertUsable();
        this.setState('joining');
        const transport = {
            type: options.transport.type,
            livekit_service_url: options.transport.livekit_service_url,
            livekit_alias: hashedRoomName(this.roomId),
        };
        try {
            this.session.joinRTCSession({
                userId: this.localUserId,
                deviceId: this.localDeviceId,
                memberId: memberIdFor(this.localUserId, this.localDeviceId),
            }, [], transport, {
                notificationType: options.notify === 'none' ? undefined : options.notify,
                callIntent: options.intent,
                manageMediaKeys: true,
                unstableSendStickyEvents: false,
                delayedLeaveEventDelayMs: MEMBERSHIP_TIMING.delayedLeaveEventDelayMs,
                delayedLeaveEventRestartMs: MEMBERSHIP_TIMING.delayedLeaveEventRestartMs,
                keyRotationGracePeriodMs: MEMBERSHIP_TIMING.keyRotationGracePeriodMs,
                useKeyDelay: MEMBERSHIP_TIMING.useKeyDelay,
            });
        }
        catch (error) {
            this.setState('error');
            throw toRtcError(error, 'membership_failed');
        }
    }
    async rejoin(options) {
        this.assertUsable();
        try {
            await this.session.leaveRoomSession(MEMBERSHIP_TIMING.rejoinLeaveTimeoutMs);
        }
        catch (error) {
            this.logger.warn('rtc.signalling.rejoin_leave_failed', { error: String(error) });
        }
        await this.join({ ...options, notify: 'none' });
    }
    async leave(timeoutMs = 5000) {
        if (!this.session.isJoined()) {
            this.setState('left');
            return;
        }
        try {
            await this.session.leaveRoomSession(timeoutMs);
        }
        finally {
            this.setState('left');
        }
    }
    async decline(notificationEventId) {
        this.assertUsable();
        await this.options.client.sendRtcDecline(this.roomId, notificationEventId);
    }
    on(event, listener) {
        return this.emitter.on(event, listener);
    }
    dispose() {
        if (this.disposed) {
            return;
        }
        this.disposed = true;
        this.subscriptions.forEach(({ event, listener }) => this.session.off(event, listener));
        this.subscriptions.length = 0;
        this.emitter.removeAll();
    }
    get slotId() {
        return RTC_PROTOCOL_V1.slotId;
    }
    subscribe(event, listener) {
        this.session.on(event, listener);
        this.subscriptions.push({ event, listener });
    }
    setState(next) {
        if (this.state === next) {
            return;
        }
        this.state = next;
        this.emitter.emit('joinState', next);
    }
    assertUsable() {
        if (this.disposed) {
            throw new RtcError('cancelled', 'Signalling was disposed');
        }
    }
}
